import { styled } from "../shared/Template.styles";
import {
    useGetRoleQuery,
    useUpdateRoleMutation,
    useGetPermissionsQuery,
} from "@/src/graphql/generated/api/graphql";
import {
    CircularProgress,
    Stack,
    TextField,
    Box,
    Snackbar,
    Alert,
    Autocomplete,
    Checkbox,
    Button,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { ErrorMessage } from "@hookform/error-message";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

const RoleDetailTemplate = () => {
    const { t } = useTranslation("common", {
        keyPrefix: "role-detail",
    });

    const { t: tPermissionKey, i18n } = useTranslation("common", {
        keyPrefix: "permission-keys",
    });

    const { t: tPermissionGroup, i18n: i18nGroup } = useTranslation("common", {
        keyPrefix: "permission-groups",
    });

    const { id: roleId } = useParams({ strict: false }) as { id: string };
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const schema = yup
        .object({
            role: yup.object().shape({
                name: yup.string().required().label(t("role-name")),
                permissions: yup
                    .array()
                    .of(yup.string())
                    .min(1)
                    .required()
                    .label(t("permissions")),
            }),
        })
        .required();

    const form = useForm<yup.InferType<typeof schema>>({
        resolver: yupResolver(schema),
        reValidateMode: "onChange",
        mode: "onChange",
    });

    const role = form.watch("role");

    const {
        data,
        loading: queryLoading,
        error,
    } = useGetRoleQuery({
        variables: { getRoleId: roleId },
    });

    const { data: permissionsData } = useGetPermissionsQuery({
        variables: {
            paginationInput: { page: 0, pageSize: 100 },
            filters: [],
            sorting: [],
        },
    });

    const permissions = [...(permissionsData?.getPermissions.rows ?? [])].sort(
        (a, b) =>
            tPermissionGroup(a.groupName).localeCompare(
                tPermissionGroup(b.groupName)
            )
    );

    useEffect(() => {
        if (data?.getRole) {
            form.reset({
                role: {
                    name: data.getRole.name,
                    permissions: data.getRole.permissions.map((p) => p.id),
                },
            });
        }
    }, [data, form]);

    const handleCloseSnackbar = (
        event?: React.SyntheticEvent | Event,
        reason?: string
    ) => {
        if (reason === "clickaway") {
            return;
        }
        setSnackbarOpen(false);
    };

    const onSubmit = form.handleSubmit(
        () => {
            setLoading(true);
            updateRoleMutation();
        },
        (_error) => {
            setErrorMessage(t("validation-error"));
            setSnackbarOpen(true);
        }
    );

    const [updateRoleMutation] = useUpdateRoleMutation({
        variables: {
            input: {
                id: roleId,
                name: role?.name,
                permissions: role?.permissions ?? [],
            },
        },
        refetchQueries: ["GetRoles"],
        onCompleted: () => {
            setLoading(false);
            navigate({ to: "/admin/roles" });
        },
        onError: (_error) => {
            setLoading(false);
            setErrorMessage(t("update-failed"));
            setSnackbarOpen(true);
        },
    });

    if (queryLoading) return <CircularProgress />;

    if (error || !data?.getRole) {
        console.error("Error fetching role detail:", error);
        return <div>{t("role-not-found", { roleId })}</div>;
    }

    const { name } = data.getRole;

    return (
        <>
            <styled.Container>
                <styled.Header title={t("role-name-title", { name })} />
                <styled.Main>
                    <form onSubmit={onSubmit} data-testid="form">
                        <Stack spacing={4} direction="column">
                            <TextField
                                {...form.register("role.name")}
                                id="roleName"
                                label={t("role-name")}
                                error={!!form.formState.errors.role?.name}
                                helperText={
                                    <ErrorMessage
                                        errors={form.formState.errors}
                                        name="role.name"
                                    />
                                }
                                fullWidth
                            />
                            <Autocomplete
                                multiple
                                id="permissions"
                                options={permissions}
                                disableCloseOnSelect
                                groupBy={(option) =>
                                    i18nGroup.exists(
                                        `permission-groups.${option.groupName}`
                                    )
                                        ? tPermissionGroup(option.groupName)
                                        : option.groupName
                                }
                                getOptionLabel={(option) =>
                                    i18n.exists(`permission-keys.${option.key}`)
                                        ? tPermissionKey(option.key)
                                        : option.name
                                }
                                isOptionEqualToValue={(option, value) =>
                                    option.id === value.id
                                }
                                value={permissions.filter((p) =>
                                    role?.permissions?.includes(p.id)
                                )}
                                onChange={(_event, newValue) => {
                                    form.setValue(
                                        "role.permissions",
                                        newValue.map((p) => p.id),
                                        { shouldValidate: true }
                                    );
                                }}
                                renderOption={(props, option, { selected }) => {
                                    const { key, ...optionProps } = props;
                                    return (
                                        <li key={key} {...optionProps}>
                                            <Checkbox
                                                icon={
                                                    <CheckBoxOutlineBlankIcon fontSize="small" />
                                                }
                                                checkedIcon={
                                                    <CheckBoxIcon fontSize="small" />
                                                }
                                                style={{ marginRight: 8 }}
                                                checked={selected}
                                            />
                                            {i18n.exists(
                                                `permission-keys.${option.key}`
                                            )
                                                ? tPermissionKey(option.key)
                                                : option.name}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label={t("permissions")}
                                        error={
                                            !!form.formState.errors.role
                                                ?.permissions
                                        }
                                        helperText={
                                            <ErrorMessage
                                                errors={form.formState.errors}
                                                name="role.permissions"
                                            />
                                        }
                                    />
                                )}
                                fullWidth
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                    mt: 2,
                                }}
                            >
                                <Button
                                    type="submit"
                                    loading={loading}
                                    variant="contained"
                                    color="primary"
                                    disabled={!form.formState.isValid}
                                    data-testid="submit-button"
                                >
                                    {t("save")}
                                </Button>
                            </Box>
                        </Stack>
                    </form>
                </styled.Main>
            </styled.Container>

            <Snackbar
                open={snackbarOpen}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity="error"
                    sx={{ width: "100%" }}
                >
                    {errorMessage}
                </Alert>
            </Snackbar>
        </>
    );
};

export default RoleDetailTemplate;
